import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles } from '@/mocks/articles';
import {
  SITE_URL,
  HOME_KEYWORDS,
  upsertMeta,
  removeMeta,
  upsertCanonical,
} from '@/lib/seo';

const HOME_FALLBACK_TITLE = 'CSSBuy Spreadsheet 2026: Guias, Links e Recursos Atualizados';
const HOME_FALLBACK_DESC = 'Explore recursos de CSSBuy Spreadsheet, QC, envio, warehouse, reviews e comparações.';

export default function SeoManager() {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const path = location.pathname;
    const lang = i18n.language || 'pt';

    let title: string;
    let description: string;
    let keywords: string;
    let canonical: string;
    let ogType: string;
    let image: string | undefined;

    if (path === '/') {
      title = t('meta_home_title') || HOME_FALLBACK_TITLE;
      description = t('meta_home_desc') || HOME_FALLBACK_DESC;
      keywords = HOME_KEYWORDS;
      canonical = SITE_URL;
      ogType = 'website';
      image = undefined;
    } else {
      const match = path.match(/^\/article\/([^/]+)/);
      const article = match ? articles.find((a) => a.id === match[1]) : undefined;

      if (match && article) {
        title = t(article.titleKey);
        description = t(article.descKey);
        keywords = `${title}, CSSBuy Spreadsheet, CSSBuy Guide, ${lang}`;
        canonical = `${SITE_URL}/article/${article.id}`;
        ogType = 'article';
        image = article.image;
      } else if (match && !article) {
        // Unknown article slug falls back to home metadata
        title = t('meta_home_title') || HOME_FALLBACK_TITLE;
        description = t('meta_home_desc') || HOME_FALLBACK_DESC;
        keywords = HOME_KEYWORDS;
        canonical = SITE_URL;
        ogType = 'website';
        image = undefined;
      } else {
        title = '404 - Page Not Found';
        description = 'The page you are looking for could not be found.';
        keywords = '';
        canonical = SITE_URL;
        ogType = 'website';
        image = undefined;
      }
    }

    document.title = title;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords);
    upsertCanonical(canonical);

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:type', ogType);
    if (image) {
      upsertMeta('property', 'og:image', image);
    } else {
      removeMeta('property', 'og:image');
    }

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    if (image) {
      upsertMeta('name', 'twitter:image', image);
    } else {
      removeMeta('name', 'twitter:image');
    }
  }, [location.pathname, i18n.language, t]);

  return null;
}